/**
 * FormLoader — transforms the 3-file JSON standard into FormSchema.
 *
 * Pipeline: domain.json + orch.json → fields + layout, rules.json → effects/observers/rules
 *
 * Separation of concerns:
 *   domain.json — data contract: types, validation, widget hints, actionId, conditions
 *   orch.json   — UI layout: section grouping, field sizing (minCols/maxCols/minWidthPx/maxWidthPx)
 *   rules.json  — runtime behavior: effects, observers, validation rules
 *
 * This is a DIRECT pipeline — no legacy adapters, no conversion layers.
 * Reads JSON Schema 2020-12 domain properties natively.
 */

import { z } from 'zod';
import type {
  DomainSchemaFiles,
  FormSchemaFiles,
  RawDomainSchema,
  RawDomainSchemaProperty,
  RawOrchSchema,
  RawOrchSection,
  RawOrchUdfEntry,
  RawOrchEachSection,
  RawOrchDataSourceEntry,
  RawOrchColumnGroup,
  RawOrchStep,
  RawOrchLayoutEntry,
  RawOrchElement,
  RawOrchEachItemField,
  RawArrayRuleEntry,
  RawRulesFile,
  FormLoaderContext,
  ValidationIssue,
} from './types';
import type { FormSchema, FormMode, FormMetadata, FormFeatures, FormSubmit, RepeatableCardConfig } from '../types/schema';
import type { FormBlock, BlockSlot } from '../types/block';
import { resolveFileUploadConfig } from '../utils/fileUploadConfig';
import type { FormFieldMapping, FieldDef, FieldWhen, SelectOption } from '../types/fieldMapping';
import type { FormFieldsNode, FormSectionDividerEntry, FormInlineEachEntry, FormSpacerEntry, FormFieldStackEntry, FormLayoutNode, FormUdfNode, FormEmbeddedTableNode, FormEachNode, FormConditionalNode, FormColumnGroupNode, FormColumnGroupItem, EachPresentation } from '../types/layout';
import type { FormStep, StepTransition } from '../types/step';
// FormUdfNode is used both for the top-level 'udf' case and for inlineUdfNodes embedding.
import type { Condition } from '../types/conditions';
import type { Compute } from '../types/compute';
import { expandRules } from './RulesExpander';
import { resolveRef } from './uriResolver';
import { resolveDomainRefs } from './resolveDomainRefs';
import { expandOrchSchema } from './resolveOrchRefs';
import { getBooleanKeyword, getValidCharsPreset, resolveMessage } from './validationKeywords';
import { getPatternPreset } from './patternRegistry';
import { INPUT_FORMAT_FNS } from '../processors/inputFormatters';
import { resolvePropertyDisplayFormat } from '../utils/displayFormatKeys';
import { resolveInputPrecision, parseDateLocal } from '@packages/core';
import { buildFormPrecisionConfig } from '../precision/precisionContract';
import type { FormPrecisionConfig } from '../precision/precisionContract';
import { buildShellPresentation } from '../utils/shellPresentation';

export interface DomainSchemaResult {
  schema: FormSchema;
  issues: ValidationIssue[];
}

// ─── JSON Schema type → adapter hint mapping ────────────────────────

const FORMAT_TO_ADAPTER: Record<string, string> = {
  date: 'date',
  'date-time': 'date',
  email: 'text',
  uuid: 'text',
};

const WIDGET_ALIAS: Record<string, string> = {
  input: 'text',
  maskedInput: 'text',
  password: 'password',
};

function inferAdapter(prop: RawDomainSchemaProperty): string {
  if (prop.fileUpload) return 'file-upload';
  if (prop.widget) {
    const w = prop.widget;
    return WIDGET_ALIAS[w] ?? w;
  }
  if (prop.type === 'file') return 'file';
  if (prop.type === 'boolean') return 'checkbox';
  if (prop.type === 'number' || prop.type === 'integer') return 'number';
  if (prop.format && FORMAT_TO_ADAPTER[prop.format]) {
    return FORMAT_TO_ADAPTER[prop.format];
  }
  return 'text';
}

// ─── domain.json property → FieldDef ─────────────────────────────────

function buildZodSchemaFromProperty(prop: RawDomainSchemaProperty): z.ZodTypeAny {
  if (prop.type === 'boolean') {
    if (prop.widget === 'radio') {
      return z.enum(['true', 'false']).nullable();
    }
    return z.boolean().nullable();
  }
  if (prop.type === 'file') {
    if (prop.fileUpload) {
      return z.array(z.any()).optional().default([]);
    }
    // Simple file pickers store FileList / File in RHF.
    return z.any();
  }
  if (prop.type === 'number' || prop.type === 'integer') {
    const inputPrecision = resolveInputPrecision({
      fieldInputPrecision: prop.inputPrecision,
      fieldType: prop.type,
    });
    let s: z.ZodTypeAny = z.number();
    if (prop.minimum != null) s = (s as z.ZodNumber).min(prop.minimum);
    if (prop.maximum != null) s = (s as z.ZodNumber).max(prop.maximum);
    if (inputPrecision >= 0) {
      s = s.refine(
        (val) => {
          if (typeof val !== 'number' || !Number.isFinite(val)) return false;
          const factor = 10 ** inputPrecision;
          return Math.round(val * factor) / factor === val;
        },
        { message: `Maximum ${inputPrecision} decimal places allowed` },
      );
    }
    return s.nullable();
  }
  if (prop.type === 'array' || prop.isMulti === true) {
    // Use z.any() for array items — the item type (string UUID, numeric enum, boolean flag)
    // is owned by the backend API contract, not by the form engine's Zod schema.
    // Examples: companyTypes → number[] (enum), featureSetIds → string[] (UUID),
    //           useDefaultPsp options → boolean (true/false).
    // Zod's job here is only to validate that the field IS an array and (if required)
    // that it is non-empty. Item-level type validation belongs in the payload builder.
    return z.array(z.any()).optional().default([]);
  }
  let s = z.string();
  if (prop.minLength != null) s = s.min(prop.minLength);
  if (prop.maxLength != null) s = s.max(prop.maxLength);
  if (prop.pattern != null) s = s.regex(new RegExp(prop.pattern));

  if (prop.format === 'email') {
    s = s.email('Invalid email address');
  }

  if (prop.exactLength != null) {
    s = s.length(prop.exactLength, `Must be exactly ${prop.exactLength} characters`);
  }

  const title = prop.title ?? '';

  for (const kw of ['noBlank', 'noDoubleSpace', 'digitsOnly'] as const) {
    if (prop[kw] === true) {
      const def = getBooleanKeyword(kw);
      if (def) s = s.regex(def.pattern, resolveMessage(def.message, title));
    }
  }

  if (typeof prop.charset === 'string') {
    const preset = getValidCharsPreset(prop.charset);
    if (preset) s = s.regex(preset.pattern, resolveMessage(preset.message, title));
  }

  if (typeof prop.patternPreset === 'string') {
    const preset = getPatternPreset(prop.patternPreset);
    if (preset) s = s.regex(preset.pattern, resolveMessage(preset.message, title));
  }

  // All base schemas are nullable so that null (the natural "no value" state
  // in RHF defaultValues / collectFieldDefaults) is always accepted.
  // validationComposer.buildFieldSchema strips nullable via unwrapZodSchema
  // before applying required constraints — so required fields still reject null.
  return s.nullable();
}

function propertyToFieldDef(prop: RawDomainSchemaProperty): FieldDef {
  const displayFormat = resolvePropertyDisplayFormat(prop);

  return {
    label: prop.title ?? '',
    adapterHint: inferAdapter(prop),
    schema: buildZodSchemaFromProperty(prop),
    ...(prop.type != null && { type: prop.type }),
    ...(displayFormat != null && { displayFormat }),
    ...(prop.displayPrecision != null && { displayPrecision: prop.displayPrecision }),
    ...(prop.inputPrecision != null && { inputPrecision: prop.inputPrecision }),
    ...(prop.placeholder != null && { placeholder: prop.placeholder }),
    ...(prop.adapterProps != null && typeof prop.adapterProps === 'object' && { adapterProps: prop.adapterProps }),
  };
}

function elementScope(el: RawOrchElement): string {
  return el.scope ?? el.$ref ?? '';
}

function resolvePropertyActionId(prop: RawDomainSchemaProperty): string | undefined {
  if (typeof prop.actionId === 'string') return prop.actionId;
  const ds = prop.dataSource;
  if (ds && typeof ds === 'object' && typeof (ds as { actionId?: string }).actionId === 'string') {
    return (ds as { actionId: string }).actionId;
  }
  return undefined;
}

function resolvePropertyDependsOn(prop: RawDomainSchemaProperty): string | undefined {
  if (typeof prop.dependsOn === 'string') return prop.dependsOn;
  const ds = prop.dataSource;
  if (ds && typeof ds === 'object' && typeof (ds as { dependsOn?: string }).dependsOn === 'string') {
    return (ds as { dependsOn: string }).dependsOn;
  }
  return undefined;
}

function isMultiSelectProperty(prop: RawDomainSchemaProperty): boolean {
  return prop.isMulti === true || prop.type === 'array' || prop.multiple === true;
}

function scopeToKey(scope: string): string {
  const parts = scope.split('/');
  return parts[parts.length - 1];
}

function scopeToFlatKey(scope: string): string {
  const stripped = scope.replace(/^#\/properties\//, '');
  return stripped.replace(/\/properties\//g, '.');
}

function resolveNestedProperty(
  properties: Record<string, RawDomainSchemaProperty>,
  scope: string,
): RawDomainSchemaProperty | undefined {
  const path = scope.replace(/^#\/properties\//, '');
  const segments = path.split('/properties/');

  let current: RawDomainSchemaProperty | undefined = properties[segments[0]];
  for (let i = 1; i < segments.length; i++) {
    if (!current) return undefined;
    const nested = current.properties as Record<string, RawDomainSchemaProperty> | undefined;
    if (!nested) return undefined;
    current = nested[segments[i]];
  }
  return current;
}

// ─── JSON Pointer → dot-path for condition field refs ────────────────

function pointerToPath(pointer: string): string {
  return pointer.replace(/^#\/properties\//, '').replace(/\/properties\//g, '.');
}

function normalizeConditionPaths(cond: Record<string, unknown>): Condition {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(cond)) {
    if (k === 'field' && typeof v === 'string' && v.startsWith('#/')) {
      out[k] = pointerToPath(v);
    } else if (k === 'allOf' || k === 'anyOf') {
      out[k] = (v as Record<string, unknown>[]).map((c) => normalizeConditionPaths(c));
    } else if (k === 'not' && typeof v === 'object' && v !== null) {
      out[k] = normalizeConditionPaths(v as Record<string, unknown>);
    } else {
      out[k] = v;
    }
  }
  return out as unknown as Condition;
}

/**
 * Walks ancestor properties along a nested scope path and collects any `hiddenWhen`
 * conditions. If multiple ancestors have hiddenWhen, they are merged via `allOf`.
 * Returns undefined if no ancestor has hiddenWhen.
 */
function resolveAncestorHiddenWhen(
  properties: Record<string, RawDomainSchemaProperty>,
  scope: string,
): Record<string, unknown> | undefined {
  const path = scope.replace(/^#\/properties\//, '');
  const segments = path.split('/properties/');
  if (segments.length <= 1) return undefined;

  let ancestorHidden: Record<string, unknown> | undefined;
  let current: Record<string, RawDomainSchemaProperty> = properties;

  for (let i = 0; i < segments.length - 1; i++) {
    const parent = current[segments[i]];
    if (!parent) break;
    if (parent.hiddenWhen && typeof parent.hiddenWhen === 'object') {
      ancestorHidden = ancestorHidden
        ? { allOf: [ancestorHidden, parent.hiddenWhen as Record<string, unknown>] }
        : (parent.hiddenWhen as Record<string, unknown>);
    }
    current = (parent.properties ?? {}) as Record<string, RawDomainSchemaProperty>;
  }
  return ancestorHidden;
}

/**
 * Checks if a nested field is required by its parent object's `required` array.
 * Only looks at the immediate parent.
 */
function resolveNestedRequired(
  properties: Record<string, RawDomainSchemaProperty>,
  scope: string,
): boolean {
  const path = scope.replace(/^#\/properties\//, '');
  const segments = path.split('/properties/');
  if (segments.length <= 1) return false;

  const leafKey = segments[segments.length - 1];
  let current: Record<string, RawDomainSchemaProperty> = properties;

  for (let i = 0; i < segments.length - 1; i++) {
    const parent = current[segments[i]];
    if (!parent) return false;
    if (i === segments.length - 2) {
      const parentRequired = (parent as Record<string, unknown>).required as string[] | undefined;
      return parentRequired?.includes(leafKey) ?? false;
    }
    current = (parent.properties ?? {}) as Record<string, RawDomainSchemaProperty>;
  }
  return false;
}

// ─── Build a single field mapping (reads domain for widget/conditions) ───

function buildFieldMapping(
  el: RawOrchElement,
  properties: Record<string, RawDomainSchemaProperty>,
  requiredSet: Set<string>,
  issues: ValidationIssue[],
): FormFieldMapping | null {
  const scope = elementScope(el);
  if (!scope) {
    issues.push({
      severity: 'warning',
      path: 'orch.field',
      message: '[orch] field entry missing scope/$ref',
    });
    return null;
  }

  const prop = resolveNestedProperty(properties, scope);

  if (!prop) {
    issues.push({
      severity: 'warning',
      path: scope,
      message: `[orch] scope "${scope}" references unknown property`,
    });
    return null;
  }

  const fieldKey = scopeToFlatKey(scope);
  const def = propertyToFieldDef(prop);

  const resolvedActionId = resolvePropertyActionId(prop);
  const hasActionId = resolvedActionId != null;

  let adapterHint = def.adapterHint ?? 'text';
  // search-picker is a first-class widget (Participant Name panel). Also accept
  // presentation: "panel" on select+actionId as an alias that promotes here.
  const isSearchPicker =
    adapterHint === 'search-picker' ||
    (adapterHint === 'select' && prop.presentation === 'panel');
  if (isSearchPicker) {
    adapterHint = 'search-picker';
    def.adapterHint = 'search-picker';
  } else if (adapterHint === 'select' && hasActionId) {
    adapterHint = 'async-select';
    def.adapterHint = 'async-select';
  }
  // multi-select: promote type:array + select + actionId → async-select with isMulti
  const isMultiField = isMultiSelectProperty(prop);

  let fieldOptions: SelectOption[] | undefined;
  if (prop.options && Array.isArray(prop.options)) {
    const isBoolRadio = prop.type === 'boolean' && inferAdapter(prop) === 'radio';
    fieldOptions = prop.options.map((o) => ({
      label: o.label,
      value: isBoolRadio ? String(o.value) : o.value,
    }));
  }

  const fieldWhen: FieldWhen = {};

  // hiddenWhen → inverted to when.visible = { not: condition }
  if (prop.hiddenWhen && typeof prop.hiddenWhen === 'object') {
    const normalizedHidden = normalizeConditionPaths(
      prop.hiddenWhen as Record<string, unknown>,
    );
    fieldWhen.visible = { not: normalizedHidden } as unknown as Condition;
  }

  // Inherit ancestor object hiddenWhen — if a parent `type: "object"` has hiddenWhen,
  // all nested fields inherit it (merged with the field's own hiddenWhen via allOf).
  if (!fieldWhen.visible) {
    const ancestorHidden = resolveAncestorHiddenWhen(properties, scope);
    if (ancestorHidden) {
      const normalizedAncestor = normalizeConditionPaths(ancestorHidden);
      fieldWhen.visible = { not: normalizedAncestor } as unknown as Condition;
    }
  } else {
    // Field has its own hiddenWhen — check if ancestor also has one and merge
    const ancestorHidden = resolveAncestorHiddenWhen(properties, scope);
    if (ancestorHidden) {
      const mergedHidden = prop.hiddenWhen
        ? { allOf: [ancestorHidden, prop.hiddenWhen as Record<string, unknown>] }
        : ancestorHidden;
      const normalizedMerged = normalizeConditionPaths(mergedHidden);
      fieldWhen.visible = { not: normalizedMerged } as unknown as Condition;
    }
  }

  // disabledWhen → when.disabled = condition (field greyed out, still visible)
  if (prop.disabledWhen && typeof prop.disabledWhen === 'object') {
    fieldWhen.disabled = normalizeConditionPaths(
      prop.disabledWhen as Record<string, unknown>,
    );
  }

  // requiredWhen → when.required = condition (conditional required without rules.json)
  // Use for inline required tied to a sibling field value.
  // For complex cross-field rules or mode-scoped required, use rules.json instead.
  if (prop.requiredWhen && typeof prop.requiredWhen === 'object') {
    fieldWhen.required = normalizeConditionPaths(
      prop.requiredWhen as Record<string, unknown>,
    );
  }

  // readOnly → when.readOnly = true (always non-editable — cascaded/computed fields)
  // readOnlyWhen → when.readOnly = condition (field shows value but cannot be edited)
  // Prefer modes.edit.properties.field.readOnly for fields always read-only in edit mode only.
  if (prop.readOnly === true) {
    fieldWhen.readOnly = true;
  }
  if (prop.readOnlyWhen && typeof prop.readOnlyWhen === 'object') {
    fieldWhen.readOnly = normalizeConditionPaths(
      prop.readOnlyWhen as Record<string, unknown>,
    );
  }

  // adapterHintWhen → when.adapterHint = { condition, trueHint, falseHint }
  // Swaps the UI widget (adapter) for the same field based on a condition — e.g. select↔text for
  // US state dropdown vs non-US free-form text. Does NOT change data fetching; use two separate
  // hiddenWhen fields when you need different actionId per widget variant.
  if (
    prop.adapterHintWhen &&
    typeof prop.adapterHintWhen === 'object' &&
    'condition' in prop.adapterHintWhen &&
    'trueHint' in prop.adapterHintWhen &&
    'falseHint' in prop.adapterHintWhen
  ) {
    const ahw = prop.adapterHintWhen as { condition: Record<string, unknown>; trueHint: string; falseHint: string };
    fieldWhen.adapterHint = {
      condition: normalizeConditionPaths(ahw.condition),
      trueHint: ahw.trueHint,
      falseHint: ahw.falseHint,
    };
  }

  // clearOnHideWhen → when.clearOnHide = boolean
  // Opt out of the default "clear value when hidden" behavior.
  // Default (omitted) = true (value is cleared when field transitions to hidden).
  // Set false to retain the value while hidden — useful for observer-computed fields.
  if (prop.clearOnHideWhen === false) {
    fieldWhen.clearOnHide = false;
  }

  // Wire a synchronous `onChange` formatter from `inputFormatter` + `inputFormatterWhen`.
  // This eliminates the one-frame flicker caused by async effectProcessor formatting.
  // The async effect remains but becomes a no-op (idempotent) since `onChange` already
  // formats the value before it enters RHF state.
  if (prop.inputFormatter) {
    const syncFn = INPUT_FORMAT_FNS[prop.inputFormatter];
    if (syncFn) {
      if (prop.inputFormatterWhen && typeof prop.inputFormatterWhen === 'object') {
        const normalizedCond = normalizeConditionPaths(prop.inputFormatterWhen as Record<string, unknown>);
        const falseFn = prop.inputFormatterFalse ? INPUT_FORMAT_FNS[prop.inputFormatterFalse] : undefined;
        fieldWhen.formatter = {
          condition: normalizedCond,
          trueFormatter: syncFn,
          falseFormatter: falseFn,
        };
      } else {
        // Unconditional formatter — apply on all keystrokes
        def.formatter = syncFn;
      }
    }
  }

  const hasWhen = Object.keys(fieldWhen).length > 0;

  const adapterProps: Record<string, unknown> = {};
  if (el.groupWith) adapterProps.groupWith = el.groupWith;
  if (el.groupLabel) adapterProps.groupLabel = el.groupLabel;
  const dependsOn = resolvePropertyDependsOn(prop);
  if (dependsOn) adapterProps.dependsOn = dependsOn;
  if (dependsOn) {
    adapterProps.resetOnParamsChange = true;
  }
  if (prop.paramMap && typeof prop.paramMap === 'object') {
    adapterProps.paramMap = prop.paramMap;
  }
  // Forward multiVariant for multi-select fields so RhfAsyncSelect renders CheckboxSelect
  if (isMultiField && prop.multiVariant) {
    adapterProps.multiVariant = prop.multiVariant;
  }
  const resolvedFileUpload = resolveFileUploadConfig(prop);
  if (resolvedFileUpload) {
    adapterHint = 'file-upload';
    def.adapterHint = 'file-upload';
  }

  if (prop.type === 'file') {
    if (typeof prop.accept === 'string') adapterProps.accept = prop.accept;
    if (typeof prop.maxSize === 'number') adapterProps.maxSize = prop.maxSize;
  }

  // Forward input addon labels (e.g. "$", "DAYS", "%") for InputGroup.AttachedAddon
  if (prop.startAddon) adapterProps.startAddon = prop.startAddon;
  if (prop.endAddon) adapterProps.endAddon = prop.endAddon;
  // Forward date constraints for date-picker fields (parse strings as local time)
  if (prop.maxDate) adapterProps.maxDate = prop.maxDate === 'today' ? new Date() : parseDateLocal(prop.maxDate);
  if (prop.minDate) adapterProps.minDate = prop.minDate === 'today' ? new Date() : parseDateLocal(prop.minDate);
  // SearchPicker-only props — `columns` here is panel grid columns, NEVER matrix sizing
  if (adapterHint === 'search-picker') {
    if (typeof prop.columns === 'number') adapterProps.columns = prop.columns;
    if (typeof prop.subtitleField === 'string') adapterProps.subtitleField = prop.subtitleField;
    if (typeof prop.subtitlePrefix === 'string') adapterProps.subtitlePrefix = prop.subtitlePrefix;
  }
  const hasAdapterProps = Object.keys(adapterProps).length > 0;

  // ─── View-mode masking — propagated from domain property to FormFieldMapping ─
  // Mirrors the same extraction in ViewLoader so that label-value and view-fields
  // presentation modes both gain eye-icon / MaskedValue support from the same schema.
  const viewDisplayOverrides: Record<string, unknown> = {};
  if (prop.masked != null) viewDisplayOverrides.masked = prop.masked;
  if (prop.maskedPlaceholder != null) viewDisplayOverrides.maskedPlaceholder = prop.maskedPlaceholder;
  if (prop.unmaskActionId != null) viewDisplayOverrides.unmaskActionId = prop.unmaskActionId;
  if (prop.maskedFormatKey != null) viewDisplayOverrides.maskedFormatKey = prop.maskedFormatKey;
  const hasViewDisplayOverrides = Object.keys(viewDisplayOverrides).length > 0;

  const minCols = el.minCols ?? el.colSpan ?? 1;
  const maxCols = el.maxCols ?? el.colSpan ?? 1;
  const shellPresentation = buildShellPresentation(el);

  return {
    name: fieldKey,
    def,
    required: requiredSet.has(scopeToKey(scope)) || resolveNestedRequired(properties, scope),
    ...(prop.widget === 'hidden' && { hidden: true }),
    matrix: {
      minCols,
      maxCols,
      ...(el.minWidthPx != null && { minWidthPx: el.minWidthPx }),
      ...(el.maxWidthPx != null && { maxWidthPx: el.maxWidthPx }),
      ...(el.startNewRow === true && { startNewRow: true }),
    },
    ...(hasActionId && { actionId: resolvedActionId }),
    ...(fieldOptions != null && { options: fieldOptions }),
    ...(hasWhen && { when: fieldWhen }),
    ...(hasAdapterProps && { adapterProps }),
    ...(isMultiField && { isMulti: true }),
    ...(isMultiField && prop.arrayItemKey && { arrayItemKey: prop.arrayItemKey as string }),
    ...(prop.default !== undefined && { defaultValue: prop.default }),
    ...(prop.isClearable === true && { isClearable: true }),
    ...(hasViewDisplayOverrides && { viewDisplayOverrides }),
    ...(resolvedFileUpload && { fileUpload: resolvedFileUpload }),
    ...(shellPresentation != null && { shellPresentation }),
    ...(prop.permissions != null &&
      typeof prop.permissions === 'object' &&
      !Array.isArray(prop.permissions) && { permissions: prop.permissions as Record<string, string> }),
    ...(typeof prop.disabledDisplayField === 'string' && { disabledDisplayField: prop.disabledDisplayField }),
  };
}

// ─── Build layout: flat fields node with inline section-dividers ─────

function buildLayout(
  orch: RawOrchSchema,
  domain: RawDomainSchema,
  requiredSet: Set<string>,
  issues: ValidationIssue[],
  arrayRulesMap?: Record<string, RawArrayRuleEntry>,
): FormLayoutNode[] {
  if (!orch.layout) return [];
  const properties = (domain.properties ?? {}) as Record<string, RawDomainSchemaProperty>;

  type FieldEntry = FormFieldMapping | FormSectionDividerEntry | FormInlineEachEntry | FormSpacerEntry | FormFieldStackEntry;
  let flatFields: FieldEntry[] = [];
  let udfNodes: FormUdfNode[] = [];
  let currentColumns: number | undefined;
  const result: FormLayoutNode[] = [];

  const flushFields = (): void => {
    if (flatFields.length === 0 && udfNodes.length === 0) return;
    const fieldsNode: FormFieldsNode = {
      type: 'fields',
      fields: flatFields,
      ...(currentColumns != null && { columns: currentColumns }),
      ...(udfNodes.length > 0 && { inlineUdfNodes: udfNodes }),
    };
    result.push(fieldsNode);
    flatFields = [];
    udfNodes = [];
    currentColumns = undefined;
  };

  for (const entry of orch.layout) {
    if ((entry as RawOrchUdfEntry).type === 'udf') {
      const udfEntry = entry as RawOrchUdfEntry;
      const udfNode: FormUdfNode = {
        type: 'udf',
        moduleId: udfEntry.moduleId,
        actionId: udfEntry.actionId,
        ...(udfEntry.title != null && { title: udfEntry.title }),
        ...(udfEntry.columns != null && { columns: udfEntry.columns }),
        ...(udfEntry.fieldMinWidthPx != null && { fieldMinWidthPx: udfEntry.fieldMinWidthPx }),
        ...(udfEntry.fieldMaxWidthPx != null && { fieldMaxWidthPx: udfEntry.fieldMaxWidthPx }),
        ...(udfEntry.fieldPrefix != null && { fieldPrefix: udfEntry.fieldPrefix }),
      };
      if (flatFields.length > 0) {
        udfNodes.push(udfNode);
      } else {
        const lastFieldsNode = [...result].reverse().find(
          (n): n is FormFieldsNode => n.type === 'fields' && !('dataSource' in n && n.dataSource),
        );
        if (lastFieldsNode) {
          lastFieldsNode.inlineUdfNodes = [...(lastFieldsNode.inlineUdfNodes ?? []), udfNode];
        } else {
          udfNodes.push(udfNode);
        }
      }
      continue;
    }

    if ((entry as RawOrchDataSourceEntry).type === 'fields' && (entry as RawOrchDataSourceEntry).dataSource) {
      flushFields();
      const dsEntry = entry as RawOrchDataSourceEntry;
      result.push({
        type: 'fields',
        dataSource: dsEntry.dataSource,
        ...(dsEntry.searchable != null && { searchable: dsEntry.searchable }),
        ...(dsEntry.groupSelectAll != null && { groupSelectAll: dsEntry.groupSelectAll }),
        ...(dsEntry.title != null && { title: dsEntry.title }),
        ...(dsEntry.showHeaders != null && { showHeaders: dsEntry.showHeaders }),
        ...(dsEntry.rowSeparator != null && { rowSeparator: dsEntry.rowSeparator }),
      } as FormFieldsNode);
      continue;
    }

    if ((entry as RawOrchEachSection).type === 'each') {
      const eachEntry = entry as RawOrchEachSection;
      const builtNode = buildEachNode(eachEntry, domain, issues, arrayRulesMap);
      if (!builtNode) continue;

      const canInline =
        shouldInlineEachPresentation(eachEntry.presentation) && builtNode.type === 'each';

      if (canInline) {
        if (eachEntry.title) {
          flatFields.push({
            type: 'section-divider',
            title: eachEntry.title,
            titleVariant: 'heading',
            divider: false,
            ...(eachEntry.titleStyle != null && { titleStyle: eachEntry.titleStyle }),
            ...(eachEntry.lozengeVariant != null && { lozengeVariant: eachEntry.lozengeVariant }),
          });
        }
        flatFields.push({ type: 'inline-each', node: builtNode });
      } else {
        const tableRendersOwnTitle =
          eachEntry.presentation === 'table' &&
          eachEntry.addButton?.position === 'top-right' &&
          builtNode.type === 'each';
        if (eachEntry.title && !tableRendersOwnTitle) {
          flatFields.push({
            type: 'section-divider',
            title: eachEntry.title,
            titleVariant: 'heading',
            divider: false,
            ...(eachEntry.titleStyle != null && { titleStyle: eachEntry.titleStyle }),
            ...(eachEntry.lozengeVariant != null && { lozengeVariant: eachEntry.lozengeVariant }),
          });
        }
        flushFields();
        result.push(builtNode);
      }
      continue;
    }

    if ((entry as RawOrchColumnGroup).type === 'column-group') {
      flushFields();
      const cg = entry as RawOrchColumnGroup;
      if (cg.title) {
        flatFields.push({
          type: 'section-divider',
          title: cg.title,
          titleVariant: 'heading',
          divider: false,
        });
        flushFields();
      }
      const groups: FormColumnGroupItem[] = cg.columns.map((col) => {
        const groupFields: (FormFieldMapping | FormSectionDividerEntry | FormInlineEachEntry)[] = [];
        for (const el of col.fields ?? []) {
          const mapping = buildFieldMapping(el, properties, requiredSet, issues);
          if (mapping) groupFields.push(mapping);
        }
        return { title: col.title, fields: groupFields };
      });
      result.push({
        type: 'column-group',
        groups,
        ...(cg.rowId != null && { semanticId: cg.rowId }),
      } as FormColumnGroupNode);
      continue;
    }

    const section = entry as RawOrchSection;

    const sectionColumns = (section as Record<string, unknown>).columns as number | undefined;
    if (sectionColumns != null && sectionColumns !== currentColumns) {
      flushFields();
      currentColumns = sectionColumns;
    }

    if (section.title) {
      const dividerEntry: FormSectionDividerEntry = {
        type: 'section-divider',
        title: section.title,
        titleVariant: 'heading',
        divider: section.divider ?? false,
        ...(section.titleStyle != null && { titleStyle: section.titleStyle }),
        ...(section.lozengeVariant != null && { lozengeVariant: section.lozengeVariant }),
      };

      flatFields.push(dividerEntry);
    }

    for (const el of section.fields ?? []) {
      const scope = elementScope(el);
      const prop = scope ? resolveNestedProperty(properties, scope) : undefined;
      if (prop?.type === 'embedded-table' && typeof prop.schemaRef === 'string') {
        const tableNode: FormEmbeddedTableNode = {
          type: 'embedded-table',
          schemaRef: prop.schemaRef as string,
          ...(prop.title != null && { title: prop.title }),
          ...(prop.filter != null && typeof prop.filter === 'object' && { filter: prop.filter as Record<string, unknown> }),
          ...(prop.features != null && typeof prop.features === 'object' && { features: prop.features as Record<string, unknown> }),
        };
        flushFields();
        result.push(tableNode);
        continue;
      }
      if (el.type === 'spacer') {
        flatFields.push({
          type: 'spacer',
          ...(el.minCols != null || el.maxCols != null || el.minWidthPx != null || el.maxWidthPx != null
            ? {
                matrix: {
                  minCols: el.minCols ?? 1,
                  maxCols: el.maxCols ?? 1,
                  ...(el.minWidthPx != null && { minWidthPx: el.minWidthPx }),
                  ...(el.maxWidthPx != null && { maxWidthPx: el.maxWidthPx }),
                },
              }
            : undefined),
        } as import('../types/layout').FormSpacerEntry);
        continue;
      }
      if (el.type === 'stack' && Array.isArray(el.stackFields)) {
        const subMappings: FormFieldMapping[] = [];
        for (const subEl of el.stackFields) {
          const m = buildFieldMapping(subEl, properties, requiredSet, issues);
          if (m) subMappings.push(m);
        }
        if (subMappings.length > 0) {
          flatFields.push({
            type: 'stack',
            fields: subMappings,
            ...(el.minCols != null || el.maxCols != null || el.minWidthPx != null || el.maxWidthPx != null
              ? {
                  matrix: {
                    minCols: el.minCols ?? 2,
                    maxCols: el.maxCols ?? 3,
                    ...(el.minWidthPx != null && { minWidthPx: el.minWidthPx }),
                    ...(el.maxWidthPx != null && { maxWidthPx: el.maxWidthPx }),
                  },
                }
              : undefined),
            ...(el.gap != null && { gap: el.gap }),
            ...(el.style != null && { style: el.style }),
            ...(el.className != null && { className: el.className }),
          } as import('../types/layout').FormFieldStackEntry);
        }
        continue;
      }
      const mapping = buildFieldMapping(el, properties, requiredSet, issues);
      if (!mapping) continue;
      flatFields.push(mapping);
    }
  }

  flushFields();
  return result;
}

// ─── Wizard step layout: multiple top-level nodes (fields, each, udf) ─

function buildEachItemFieldMapping(
  itemField: RawOrchEachItemField,
  itemsSchema: RawDomainSchemaProperty | undefined,
  rootProperties: Record<string, RawDomainSchemaProperty>,
  issues: ValidationIssue[],
): FormFieldMapping | null {
  let prop: RawDomainSchemaProperty | undefined;
  if (itemField.$ref) {
    prop = resolveNestedProperty(rootProperties, itemField.$ref);
  } else if (itemsSchema?.properties) {
    prop = (itemsSchema.properties as Record<string, RawDomainSchemaProperty>)[itemField.name];
  }

  if (!prop) {
    issues.push({
      severity: 'warning',
      path: itemField.name,
      message: `[orch] each itemField "${itemField.name}" references unknown property`,
    });
    return null;
  }

  const def = propertyToFieldDef(prop);
  const resolvedActionId = resolvePropertyActionId(prop);
  const hasActionId = resolvedActionId != null;
  let adapterHint = def.adapterHint ?? 'text';
  const isSearchPicker =
    adapterHint === 'search-picker' ||
    (adapterHint === 'select' && prop.presentation === 'panel');
  if (isSearchPicker) {
    adapterHint = 'search-picker';
    def.adapterHint = 'search-picker';
  } else if (adapterHint === 'select' && hasActionId) {
    adapterHint = 'async-select';
    def.adapterHint = 'async-select';
  }
  const isMultiField = isMultiSelectProperty(prop);

  const fieldWhen: FieldWhen = {};
  if (prop.hiddenWhen && typeof prop.hiddenWhen === 'object') {
    fieldWhen.visible = { not: normalizeConditionPaths(prop.hiddenWhen as Record<string, unknown>) } as unknown as Condition;
  }
  if (prop.disabledWhen && typeof prop.disabledWhen === 'object') {
    fieldWhen.disabled = normalizeConditionPaths(prop.disabledWhen as Record<string, unknown>);
  }
  if (prop.requiredWhen && typeof prop.requiredWhen === 'object') {
    fieldWhen.required = normalizeConditionPaths(prop.requiredWhen as Record<string, unknown>);
  }
  if (prop.readOnly === true) {
    fieldWhen.readOnly = true;
  }
  if (prop.readOnlyWhen && typeof prop.readOnlyWhen === 'object') {
    fieldWhen.readOnly = normalizeConditionPaths(prop.readOnlyWhen as Record<string, unknown>);
  }

  if (prop.inputFormatter) {
    const syncFn = INPUT_FORMAT_FNS[prop.inputFormatter];
    if (syncFn) {
      if (prop.inputFormatterWhen && typeof prop.inputFormatterWhen === 'object') {
        const normalizedCond = normalizeConditionPaths(prop.inputFormatterWhen as Record<string, unknown>);
        const falseFn = prop.inputFormatterFalse ? INPUT_FORMAT_FNS[prop.inputFormatterFalse] : undefined;
        fieldWhen.formatter = {
          condition: normalizedCond,
          trueFormatter: syncFn,
          falseFormatter: falseFn,
        };
      } else {
        def.formatter = syncFn;
      }
    }
  }

  const dependsOn = resolvePropertyDependsOn(prop);
  const adapterProps: Record<string, unknown> = {};
  if (dependsOn) {
    adapterProps.dependsOn = dependsOn;
    adapterProps.resetOnParamsChange = true;
  }
  if (prop.paramMap && typeof prop.paramMap === 'object') {
    adapterProps.paramMap = prop.paramMap;
  }
  if (adapterHint === 'search-picker') {
    if (typeof prop.columns === 'number') adapterProps.columns = prop.columns;
    if (typeof prop.subtitleField === 'string') adapterProps.subtitleField = prop.subtitleField;
    if (typeof prop.subtitlePrefix === 'string') adapterProps.subtitlePrefix = prop.subtitlePrefix;
  }
  if (prop.maxDate) adapterProps.maxDate = prop.maxDate === 'today' ? new Date() : parseDateLocal(prop.maxDate);
  if (prop.minDate) adapterProps.minDate = prop.minDate === 'today' ? new Date() : parseDateLocal(prop.minDate);

  const minCols = itemField.minCols ?? 1;
  const maxCols = itemField.maxCols ?? 1;
  const shellPresentation = buildShellPresentation(itemField);

  return {
    name: itemField.name,
    def,
    required: prop.required === true,
    matrix: {
      minCols,
      maxCols,
      ...(itemField.minWidthPx != null && { minWidthPx: itemField.minWidthPx }),
      ...(itemField.maxWidthPx != null && { maxWidthPx: itemField.maxWidthPx }),
      ...(itemField.startNewRow === true && { startNewRow: true }),
    },
    ...(hasActionId && { actionId: resolvedActionId }),
    ...(Object.keys(fieldWhen).length > 0 && { when: fieldWhen }),
    ...(Object.keys(adapterProps).length > 0 && { adapterProps }),
    ...(isMultiField && { isMulti: true }),
    ...(shellPresentation != null && { shellPresentation }),
  };
}

function buildEachNode(
  eachEntry: RawOrchEachSection,
  domain: RawDomainSchema,
  issues: ValidationIssue[],
  arrayRulesMap?: Record<string, RawArrayRuleEntry>,
): FormLayoutNode | null {
  const properties = (domain.properties ?? {}) as Record<string, RawDomainSchemaProperty>;
  const arrayProp = resolveNestedProperty(properties, eachEntry.field);
  if (!arrayProp) {
    issues.push({
      severity: 'warning',
      path: eachEntry.field,
      message: `[orch] each field "${eachEntry.field}" references unknown property`,
    });
    return null;
  }

  const arrayKey = pointerToPath(eachEntry.field);
  const itemsSchema = arrayProp.items as RawDomainSchemaProperty | undefined;
  const itemProperties = itemsSchema?.properties as Record<string, RawDomainSchemaProperty> | undefined;
  const isPrimitiveArray =
    itemsSchema?.type === 'string' && (itemProperties == null || Object.keys(itemProperties).length === 0);

  let itemFields: FormFieldMapping[];
  if (isPrimitiveArray) {
    const orchItem = eachEntry.itemFields[0];
    const itemProp: RawDomainSchemaProperty = {
      ...itemsSchema,
      title: orchItem?.title ?? itemsSchema?.title ?? 'Value',
    };
    const def = propertyToFieldDef(itemProp);
    itemFields = [{
      name: '',
      def,
      required: itemProp.required === true || (typeof arrayProp.minItems === 'number' && arrayProp.minItems > 0),
      matrix: {
        minCols: orchItem?.minCols ?? 1,
        maxCols: orchItem?.maxCols ?? 1,
        ...(orchItem?.minWidthPx != null && { minWidthPx: orchItem.minWidthPx }),
        ...(orchItem?.maxWidthPx != null && { maxWidthPx: orchItem.maxWidthPx }),
      },
    }];
  } else {
    itemFields = eachEntry.itemFields
      .map((itemField) => buildEachItemFieldMapping(itemField, itemsSchema, properties, issues))
      .filter((f): f is FormFieldMapping => f != null);
  }

  // Resolve array-scoped rules from rules.json — keyed by the last segment of the field path
  const arrayKey_lastSegment = arrayKey.split('.').pop() ?? arrayKey;
  const arrayRule = arrayRulesMap?.[arrayKey] ?? arrayRulesMap?.[arrayKey_lastSegment];

  // Merge resetButton: orch provides presentation (icon, label), rules provide behavior (defaults, visibleWhen)
  let mergedResetButton: FormEachNode['resetButton'] | undefined;
  if (eachEntry.resetButton != null) {
    const orchBtn = eachEntry.resetButton;
    mergedResetButton = {
      ...(orchBtn.label != null && { label: orchBtn.label }),
      ...(orchBtn.icon != null && { icon: orchBtn.icon }),
      ...(orchBtn.actionId != null && { actionId: orchBtn.actionId }),
      ...(orchBtn.params != null && { params: orchBtn.params }),
      // Behavioral keys from rules.json, with orch.json fallback
      ...(arrayRule?.resetDefaults != null
        ? { defaults: arrayRule.resetDefaults as Record<string, Compute> }
        : orchBtn.defaults != null ? { defaults: orchBtn.defaults as unknown as Record<string, Compute> } : {}),
      ...(arrayRule?.resetVisibleWhen != null
        ? { visibleWhen: normalizeConditionPaths(arrayRule.resetVisibleWhen) as unknown as Condition }
        : orchBtn.visibleWhen != null ? { visibleWhen: normalizeConditionPaths(orchBtn.visibleWhen) as unknown as Condition } : {}),
      ...(arrayRule?.resetWhen != null
        ? { resetWhen: normalizeConditionPaths(arrayRule.resetWhen) as unknown as Condition }
        : {}),
    };
  }

  const eachNode: FormEachNode = {
    type: 'each',
    name: arrayKey,
    itemFields,
    ...(eachEntry.title != null && { title: eachEntry.title }),
    ...(isPrimitiveArray && { primitiveItem: true }),
    ...(typeof eachEntry.minItems === 'number' && { minItems: eachEntry.minItems }),
    ...(typeof eachEntry.maxItems === 'number' && { maxItems: eachEntry.maxItems }),
    ...(eachEntry.minItems == null && typeof arrayProp.minItems === 'number' && { minItems: arrayProp.minItems }),
    ...(eachEntry.maxItems == null && typeof arrayProp.maxItems === 'number' && { maxItems: arrayProp.maxItems }),
    ...(eachEntry.addLabel != null && { addLabel: eachEntry.addLabel }),
    ...(eachEntry.presentation != null && { presentation: eachEntry.presentation }),
    ...(eachEntry.columns != null && { columns: eachEntry.columns }),
    ...(eachEntry.addButton != null && { addButton: eachEntry.addButton }),
    ...(eachEntry.removeButton != null && { removeButton: eachEntry.removeButton }),
    ...(mergedResetButton != null && { resetButton: mergedResetButton }),
    ...(eachEntry.removeStyle != null && { removeStyle: eachEntry.removeStyle }),
    ...(eachEntry.rowHeader != null && { rowHeader: eachEntry.rowHeader }),
    ...(eachEntry.addPosition != null && { addPosition: eachEntry.addPosition }),
    ...(eachEntry.autoSeed != null
      ? { autoSeed: eachEntry.autoSeed }
      : arrayProp.autoSeed != null && { autoSeed: arrayProp.autoSeed as boolean | number }),
    ...(eachEntry.seedFromApi != null && { seedFromApi: eachEntry.seedFromApi }),
    ...(eachEntry.sortable != null && { sortable: eachEntry.sortable }),
    ...(eachEntry.addVisibleWhen != null && { addVisibleWhen: eachEntry.addVisibleWhen }),
    ...(eachEntry.removeVisibleWhen != null && { removeVisibleWhen: eachEntry.removeVisibleWhen }),
    ...(eachEntry.defaultCollapsed != null && { defaultCollapsed: eachEntry.defaultCollapsed }),
    ...(eachEntry.showTableHeaders != null && { showTableHeaders: eachEntry.showTableHeaders }),
    ...(eachEntry.addModalId != null && { addModalId: eachEntry.addModalId }),
    ...(eachEntry.emptyState != null && { emptyState: eachEntry.emptyState }),
    ...(eachEntry.visibleWhen != null && { visibleWhen: normalizeConditionPaths(eachEntry.visibleWhen) as unknown as Record<string, unknown> }),
    // Behavioral keys: prefer rules.json arrayRules, fall back to orch.json (backwards compat).
    // JSON-sourced rules use Record<string, unknown> — cast to the stricter layout types
    // (runtime shapes are identical; the cast bridges JSON → TypeScript).
    ...(arrayRule?.arrayEffects != null
      ? { arrayEffects: arrayRule.arrayEffects as unknown as FormEachNode['arrayEffects'] }
      : eachEntry.arrayEffects != null ? { arrayEffects: eachEntry.arrayEffects as unknown as FormEachNode['arrayEffects'] } : {}),
    ...(arrayRule?.itemRowObservers != null
      ? { itemRowObservers: arrayRule.itemRowObservers as unknown as FormEachNode['itemRowObservers'] }
      : eachEntry.itemRowObservers != null ? { itemRowObservers: eachEntry.itemRowObservers as unknown as FormEachNode['itemRowObservers'] } : {}),
    ...(eachEntry.rowActions != null && { rowActions: eachEntry.rowActions }),
  };

  // domain.json `hiddenWhen` → wrap in a FormConditionalNode with an INVERTED condition.
  // hiddenWhen means "hide the block when this condition is true", so the
  // ConditionalRenderer's isVisible must be true when hiddenWhen is FALSE:
  //   visibleCondition = { not: hiddenWhenCondition }
  // Strip visibleWhen from the inner eachNode — the outer conditional handles it.
  if (arrayProp.hiddenWhen && typeof arrayProp.hiddenWhen === 'object') {
    const hiddenCondition = normalizeConditionPaths(arrayProp.hiddenWhen as Record<string, unknown>);
    // Remove redundant visibleWhen from the eachNode — the wrapper owns visibility.
    const { visibleWhen: _dropped, ...strippedEachNode } = eachNode as unknown as Record<string, unknown>;
    const conditional: FormConditionalNode = {
      type: 'conditional',
      watch: pointerToPath((arrayProp.hiddenWhen as { field?: string }).field ?? ''),
      condition: { not: hiddenCondition } as unknown as Condition,
      children: [strippedEachNode as unknown as FormEachNode],
    };
    return conditional;
  }

  // orch.json `visibleWhen` on an each entry → wrap in a FormConditionalNode.
  // Only reached when no domain hiddenWhen exists — avoids double-wrapping.
  // Strip visibleWhen from eachNode to prevent FormEachRenderer double-gating.
  if (eachEntry.visibleWhen != null) {
    const visibleCondition = normalizeConditionPaths(eachEntry.visibleWhen as Record<string, unknown>);
    const watchField = pointerToPath((eachEntry.visibleWhen as { field?: string }).field ?? '');
    const { visibleWhen: _dropped, ...strippedEachNode } = eachNode as unknown as Record<string, unknown>;
    const conditional: FormConditionalNode = {
      type: 'conditional',
      watch: watchField,
      condition: visibleCondition,
      children: [strippedEachNode as unknown as FormEachNode],
    };
    return conditional;
  }

  return eachNode;
}

/** Presentations that fit inside a flat FormFieldsNode grid via FormInlineEachEntry. */
function shouldInlineEachPresentation(presentation?: EachPresentation): boolean {
  return presentation !== 'card' && presentation !== 'accordion' && presentation !== 'table';
}

function buildStepLayoutNodes(
  layoutEntries: RawOrchLayoutEntry[] | undefined,
  domain: RawDomainSchema,
  requiredSet: Set<string>,
  issues: ValidationIssue[],
  arrayRulesMap?: Record<string, RawArrayRuleEntry>,
): FormLayoutNode[] {
  if (!layoutEntries?.length) return [];

  const properties = (domain.properties ?? {}) as Record<string, RawDomainSchemaProperty>;
  type FieldEntry = FormFieldMapping | FormSectionDividerEntry | FormInlineEachEntry | FormSpacerEntry | FormFieldStackEntry;
  const result: FormLayoutNode[] = [];
  let flatFields: FieldEntry[] = [];
  let inlineUdfNodes: FormUdfNode[] = [];

  const flushFields = (): void => {
    if (flatFields.length === 0 && inlineUdfNodes.length === 0) return;
    const fieldsNode: FormFieldsNode = {
      type: 'fields',
      fields: flatFields,
      ...(inlineUdfNodes.length > 0 && { inlineUdfNodes }),
    };
    result.push(fieldsNode);
    flatFields = [];
    inlineUdfNodes = [];
  };

  for (const entry of layoutEntries) {
    if ((entry as RawOrchUdfEntry).type === 'udf') {
      flushFields();
      const udfEntry = entry as RawOrchUdfEntry;
      result.push({
        type: 'udf',
        moduleId: udfEntry.moduleId,
        actionId: udfEntry.actionId,
        ...(udfEntry.title != null && { title: udfEntry.title }),
        ...(udfEntry.columns != null && { columns: udfEntry.columns }),
        ...(udfEntry.fieldMinWidthPx != null && { fieldMinWidthPx: udfEntry.fieldMinWidthPx }),
        ...(udfEntry.fieldMaxWidthPx != null && { fieldMaxWidthPx: udfEntry.fieldMaxWidthPx }),
        ...(udfEntry.fieldPrefix != null && { fieldPrefix: udfEntry.fieldPrefix }),
      });
      continue;
    }

    if ((entry as RawOrchDataSourceEntry).type === 'fields' && (entry as RawOrchDataSourceEntry).dataSource) {
      flushFields();
      const dsEntry = entry as RawOrchDataSourceEntry;
      const fieldsNode: FormFieldsNode = {
        type: 'fields',
        dataSource: dsEntry.dataSource,
        ...(dsEntry.searchable != null && { searchable: dsEntry.searchable }),
        ...(dsEntry.groupSelectAll != null && { groupSelectAll: dsEntry.groupSelectAll }),
        ...(dsEntry.title != null && { title: dsEntry.title }),
        ...(dsEntry.showHeaders != null && { showHeaders: dsEntry.showHeaders }),
        ...(dsEntry.rowSeparator != null && { rowSeparator: dsEntry.rowSeparator }),
      };
      result.push(fieldsNode);
      continue;
    }

    if ((entry as RawOrchEachSection).type === 'each') {
      const eachSection = entry as RawOrchEachSection;
      const builtNode = buildEachNode(eachSection, domain, issues, arrayRulesMap);
      if (!builtNode) continue;

      const canInline =
        shouldInlineEachPresentation(eachSection.presentation) && builtNode.type === 'each';

      if (canInline) {
        if (eachSection.title) {
          flatFields.push({
            type: 'section-divider',
            title: eachSection.title,
            titleVariant: 'heading',
            divider: false,
            ...(eachSection.titleStyle != null && { titleStyle: eachSection.titleStyle }),
            ...(eachSection.lozengeVariant != null && { lozengeVariant: eachSection.lozengeVariant }),
          });
        }
        flatFields.push({ type: 'inline-each', node: builtNode });
      } else {
        flushFields();
        if (eachSection.title) {
          result.push({
            type: 'fields',
            fields: [{
              type: 'section-divider',
              title: eachSection.title,
              titleVariant: 'heading',
              divider: false,
              ...(eachSection.titleStyle != null && { titleStyle: eachSection.titleStyle }),
              ...(eachSection.lozengeVariant != null && { lozengeVariant: eachSection.lozengeVariant }),
            }],
          });
        }
        result.push(builtNode);
      }
      continue;
    }

    if ((entry as RawOrchColumnGroup).type === 'column-group') {
      flushFields();
      const cg = entry as RawOrchColumnGroup;
      if (cg.title) {
        flatFields.push({
          type: 'section-divider',
          title: cg.title,
          titleVariant: 'heading',
          divider: false,
        });
        flushFields();
      }
      const groups: FormColumnGroupItem[] = cg.columns.map((col) => {
        const groupFields: (FormFieldMapping | FormSectionDividerEntry | FormInlineEachEntry)[] = [];
        for (const el of col.fields ?? []) {
          const mapping = buildFieldMapping(el, properties, requiredSet, issues);
          if (mapping) groupFields.push(mapping);
        }
        return { title: col.title, fields: groupFields };
      });
      result.push({
        type: 'column-group',
        groups,
        ...(cg.rowId != null && { semanticId: cg.rowId }),
      } as FormColumnGroupNode);
      continue;
    }

    const section = entry as RawOrchSection;
    if (section.title) {
      flatFields.push({
        type: 'section-divider',
        title: section.title,
        titleVariant: 'heading',
        divider: section.divider ?? false,
        ...(section.titleStyle != null && { titleStyle: section.titleStyle }),
        ...(section.lozengeVariant != null && { lozengeVariant: section.lozengeVariant }),
      });
    }

    for (const el of section.fields ?? []) {
      const scope = elementScope(el);
      const prop = scope ? resolveNestedProperty(properties, scope) : undefined;
      if (prop?.type === 'embedded-table' && typeof prop.schemaRef === 'string') {
        flushFields();
        result.push({
          type: 'embedded-table',
          schemaRef: prop.schemaRef as string,
          ...(prop.title != null && { title: prop.title }),
        });
        continue;
      }
      if (el.type === 'spacer') {
        flatFields.push({
          type: 'spacer',
          ...(el.minCols != null || el.maxCols != null || el.minWidthPx != null || el.maxWidthPx != null
            ? {
                matrix: {
                  minCols: el.minCols ?? 1,
                  maxCols: el.maxCols ?? 1,
                  ...(el.minWidthPx != null && { minWidthPx: el.minWidthPx }),
                  ...(el.maxWidthPx != null && { maxWidthPx: el.maxWidthPx }),
                },
              }
            : undefined),
        } as import('../types/layout').FormSpacerEntry);
        continue;
      }
      if (el.type === 'stack' && Array.isArray(el.stackFields)) {
        const subMappings: FormFieldMapping[] = [];
        for (const subEl of el.stackFields) {
          const m = buildFieldMapping(subEl, properties, requiredSet, issues);
          if (m) subMappings.push(m);
        }
        if (subMappings.length > 0) {
          flatFields.push({
            type: 'stack',
            fields: subMappings,
            ...(el.minCols != null || el.maxCols != null || el.minWidthPx != null || el.maxWidthPx != null
              ? {
                  matrix: {
                    minCols: el.minCols ?? 2,
                    maxCols: el.maxCols ?? 3,
                    ...(el.minWidthPx != null && { minWidthPx: el.minWidthPx }),
                    ...(el.maxWidthPx != null && { maxWidthPx: el.maxWidthPx }),
                  },
                }
              : undefined),
            ...(el.gap != null && { gap: el.gap }),
            ...(el.style != null && { style: el.style }),
            ...(el.className != null && { className: el.className }),
          } as import('../types/layout').FormFieldStackEntry);
        }
        continue;
      }
      const mapping = buildFieldMapping(el, properties, requiredSet, issues);
      if (mapping) flatFields.push(mapping);
    }
  }

  flushFields();
  return result;
}

function buildSteps(
  orch: RawOrchSchema,
  domain: RawDomainSchema,
  requiredSet: Set<string>,
  namespace: string,
  issues: ValidationIssue[],
  arrayRulesMap?: Record<string, RawArrayRuleEntry>,
): FormStep[] {
  if (!orch.steps?.length) return [];

  return orch.steps.map((rawStep: RawOrchStep) => {
    const layoutNodes = buildStepLayoutNodes(rawStep.layout, domain, requiredSet, issues, arrayRulesMap);
    const block: FormBlock = {
      id: `${namespace}_${rawStep.id}`,
      layout: layoutNodes,
    };

    const onNext: StepTransition | undefined = rawStep.onNext
      ? {
          ...(rawStep.onNext.actionId != null && { actionId: rawStep.onNext.actionId }),
          ...(rawStep.onNext.submit != null && { submit: rawStep.onNext.submit }),
          ...(rawStep.onNext.updateMeta != null && { updateMeta: rawStep.onNext.updateMeta }),
          ...(rawStep.onNext.nextStep != null && { nextStep: rawStep.onNext.nextStep as StepTransition['nextStep'] }),
          ...(rawStep.onNext.exitAfter != null && { exitAfter: rawStep.onNext.exitAfter }),
          ...(rawStep.onNext.label != null && { label: rawStep.onNext.label }),
          ...(rawStep.onNext.secondaryAction != null && { secondaryAction: rawStep.onNext.secondaryAction }),
        }
      : undefined;

    return {
      id: rawStep.id,
      label: rawStep.label,
      ...(rawStep.type != null && { type: rawStep.type }),
      blocks: [{ use: block, namespace }],
      ...(onNext != null && { onNext }),
      ...(rawStep.guard != null && { guard: rawStep.guard as unknown as FormStep['guard'] }),
      ...(rawStep.acknowledgements != null && { acknowledgements: rawStep.acknowledgements }),
    };
  });
}

// ─── Build modes from domain.json ────────────────────────────────────

function buildModes(
  domain: RawDomainSchema,
  orch: RawOrchSchema,
): Record<string, FormMode> {
  const modes: Record<string, FormMode> = {};
  if (!domain.modes) return modes;

  for (const [modeName, modeConfig] of Object.entries(domain.modes)) {
    const mode: FormMode = {
      ...(modeConfig.dataSource != null && {
        dataSource: modeConfig.dataSource as unknown as FormMode['dataSource'],
      }),
      ...(modeConfig.lifecycle != null && {
        lifecycle: modeConfig.lifecycle as unknown as FormMode['lifecycle'],
      }),
    };

    if (modeConfig.type === 'array') {
      mode.repeatable = {
        minItems: modeConfig.minItems,
        maxItems: modeConfig.maxItems,
      };

      const orchMode = orch.modes?.[modeName];
      if (orchMode) {
        if ('addButton' in orchMode) {
          const addBtn = orchMode.addButton as Record<string, unknown>;
          mode.repeatable.addLabel = addBtn?.label as string | undefined;
          mode.repeatable.addButton = {
            variant: addBtn?.variant as 'primary-outline' | 'primary-dark' | 'ghost' | undefined,
            icon: addBtn?.icon as string | undefined,
            fullWidth: addBtn?.fullWidth as boolean | undefined,
          };
        }
        if ('card' in orchMode) {
          const card = orchMode.card as Record<string, unknown>;
          mode.repeatable.card = {
            badgeVariant: card?.badgeVariant as RepeatableCardConfig['badgeVariant'],
            labelTemplate: card?.labelTemplate as string | undefined,
            removeLabel: card?.removeLabel as string | undefined,
            removeVariant: card?.removeVariant as RepeatableCardConfig['removeVariant'],
          };
        }
      }
    }

    if (modeConfig.properties) {
      mode.overrides = {
        fields: Object.fromEntries(
          Object.entries(modeConfig.properties)
            .filter(([, v]) => v.readOnly || v.widget === 'hidden')
            .map(([k, v]) => [k, {
              ...(v.readOnly ? { readOnly: true } : {}),
              ...(v.widget === 'hidden' ? { hidden: true } : {}),
            }]),
        ),
      };
    }

    if (modeConfig.viewPresentation) {
      mode.viewPresentation = modeConfig.viewPresentation;
    }

    modes[modeName] = mode;
  }

  // ─── Root dataSource.submit inheritance ─────────────────────────────
  // If domain.json declares a root-level dataSource.submit, merge it into
  // each non-view mode as a default. Per-mode submit fields override root.
  const rootSubmit = domain.dataSource?.submit;
  if (rootSubmit) {
    for (const [modeName, modeConfig] of Object.entries(modes)) {
      if (modeName === 'view') continue;
      if (!modeConfig.dataSource) modeConfig.dataSource = {};
      const modeSubmit = modeConfig.dataSource.submit;
      modeConfig.dataSource.submit = {
        ...rootSubmit,
        ...(modeSubmit ?? {}),
        constants: {
          ...(rootSubmit.constants ?? {}),
          ...(modeSubmit?.constants ?? {}),
        },
      } as unknown as FormSubmit;
    }
  }

  if (!modes.view) {
    modes.view = { overrides: { readOnly: true } };
  }

  return modes;
}

// ─── Orch mode page title helper ─────────────────────────────────────

function orchPageTitle(orch: RawOrchSchema, mode: string): string | undefined {
  const modeConfig = orch.modes?.[mode];
  if (modeConfig && 'page' in modeConfig) {
    const page = modeConfig.page as Record<string, unknown> | undefined;
    return page?.title as string | undefined;
  }
  return undefined;
}

// ─── Resolve $ref in domain.json properties ─────────────────────────
// See resolveDomainRefs.ts — chains module:// → shared:// → #/$defs.

// ─── Main entry point ────────────────────────────────────────────────

export function loadFormSchema(
  files: FormSchemaFiles,
  ctx: FormLoaderContext,
): DomainSchemaResult {
  return loadDomainSchema(
    { domain: files.model, orch: files.orch, rules: files.rules },
    ctx,
  );
}

export function loadDomainSchema(
  files: DomainSchemaFiles,
  ctx: FormLoaderContext,
): DomainSchemaResult {
  const issues: ValidationIssue[] = [];
  const { domain, rules } = files;
  const orch = expandOrchSchema(files.orch, ctx);

  // Resolve $ref entries in domain.properties before any field processing.
  const rawProperties = domain.properties ?? {};
  const resolvedProperties = resolveDomainRefs(
    rawProperties as Record<string, unknown>,
    ctx,
    domain as unknown as Record<string, unknown>,
  );
  const resolvedDomain: RawDomainSchema = { ...domain, properties: resolvedProperties as RawDomainSchema['properties'] };

  // Property-level `required: true` is the canonical way to mark a field required.
  // The legacy top-level `required: string[]` array is supported for backwards
  // compatibility but is deprecated — new schemas must not use it.
  const properties = (resolvedDomain.properties ?? {}) as Record<string, RawDomainSchemaProperty>;
  const requiredFromProps = Object.entries(properties)
    .filter(([, p]) => p.required === true)
    .map(([key]) => key);
  const requiredFromArray = domain.required ?? [];
  const requiredSet = new Set([...requiredFromArray, ...requiredFromProps]);

  const namespace = domain.$id?.split('/').pop() ?? orch.semanticId ?? 'main';

  // Extract array-scoped behavioral rules from rules.json
  const arrayRulesMap = (rules as RawRulesFile).arrayRules;

  const layoutNodes = buildLayout(orch, resolvedDomain, requiredSet, issues, arrayRulesMap);
  const wizardSteps = buildSteps(orch, resolvedDomain, requiredSet, namespace, issues, arrayRulesMap);

  const block: FormBlock = {
    id: orch.semanticId ?? namespace,
    layout: wizardSteps.length > 0 ? [] : layoutNodes,
  };

  const blocks: BlockSlot[] = wizardSteps.length > 0 ? [] : [{ use: block, namespace }];

  const { effects, observers, rules: formRules } = expandRules(rules, ctx);

  const modeTitles: Record<string, string> = {};
  const modeSubmitLabels: Record<string, string> = {};
  if (orch.modes) {
    for (const [modeName, modeCfg] of Object.entries(orch.modes)) {
      const mCfg = modeCfg as Record<string, unknown> | undefined;
      const mPage = mCfg?.page as Record<string, unknown> | undefined;
      if (mPage?.title) modeTitles[modeName] = mPage.title as string;
      const mSubmitBar = mCfg?.submitBar as Record<string, unknown> | undefined;
      if (mSubmitBar?.submitLabel) modeSubmitLabels[modeName] = mSubmitBar.submitLabel as string;
    }
  }

  const metadata: FormMetadata = {
    entity: domain.title ?? domain.$id ?? 'Unknown',
    title: modeTitles.create ?? domain.title ?? 'Form',
    description: domain.description,
    semanticId: orch.semanticId ?? domain.$id ?? 'form',
    ...(orch.page != null && { page: orch.page as FormMetadata['page'] }),
    ...(Object.keys(modeTitles).length > 0 && { modeTitles }),
    ...(Object.keys(modeSubmitLabels).length > 0 && { modeSubmitLabels }),
  };

  const features: FormFeatures = {
    ...(orch.wrapper != null && { wrapper: orch.wrapper as FormFeatures['wrapper'] }),
    ...(orch.submitBar != null && { submitBar: orch.submitBar as FormFeatures['submitBar'] }),
    namespace,
  };

  const modes = buildModes(resolvedDomain, orch);

  // Hidden fields (widget: "hidden") are excluded from the layout tree, so
  // collectFieldDefaults() in useFormEngineSession never picks up their `default`.
  // Collect them here and inject into each mode's defaultValues so that
  // conditions like `disabledWhen: { field: "status", notEquals: "Draft" }`
  // evaluate correctly on create (where no fetch hydrates the value).
  const hiddenDefaults: Record<string, unknown> = {};
  for (const [key, prop] of Object.entries(properties)) {
    if (prop.widget === 'hidden' && prop.default !== undefined) {
      hiddenDefaults[key] = prop.default;
    }
  }
  if (Object.keys(hiddenDefaults).length > 0) {
    for (const mode of Object.values(modes)) {
      mode.defaultValues = { ...hiddenDefaults, ...(mode.defaultValues ?? {}) };
    }
  }

  const orchDefaults = orch.defaults;
  const precision: FormPrecisionConfig = buildFormPrecisionConfig(properties, orchDefaults);

  const schema: FormSchema = {
    metadata,
    blocks,
    ...(wizardSteps.length > 0 && { steps: wizardSteps }),
    features,
    modes,
    precision,
    effects,
    observers,
    rules: formRules,
  };

  return { schema, issues };
}

export { createEmptyContext } from './createDefaultContext';

